import { Injectable } from "@angular/core";
import { PonderDataClient } from "@apiserver-rest/ponder-data-client";
import { GetNotificationsResponse, INotification, NotificationType } from "@apiserver-rest/ponder-data-client-base";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { getFromLocalStorage, LocalStorageKey } from "@ponder-shared/utils/localStorage";
import { firstValueFrom, take } from "rxjs";

/**
 * Two types of notifications:
 * 
 * 1. Global: sent to all users (including anonymous) and typically displayed as a banner.
 * 2. User: sent to specific users to alert them of events, such as a pending friend request.
 */
@Injectable({ providedIn: "root" })
export class NotificationService {
    private fetchNotificationsPromise?: Promise<INotification[]>;
    private notifications: INotification[] = [];

    constructor(private ponderDataClient: PonderDataClient, private events: EventBroker) {

    }
    
    public async getNotifications(): Promise<INotification[]> {
        if (!this.fetchNotificationsPromise) {
            this.fetchNotificationsPromise = new Promise((resolve) => {
                var readNotifcations = this.getReadGlobalNotifications();
                
                this.ponderDataClient
                    .notification()
                    .pipe(take(1))
                    .subscribe({
                        next: (response: GetNotificationsResponse) => {
                            if (response.success && response.notifications) {
                                this.notifications = response.notifications.filter(n => {
                                    return readNotifcations.indexOf(n.id) == -1;
                                });
                            }
                        }
                    })
                    .add(() => resolve(this.notifications));
            });
        }
        
        return this.fetchNotificationsPromise;
    }

    public markNotificationRead(id: string): void {
        var readNotifcations = this.getReadGlobalNotifications();

        if (readNotifcations.indexOf(id) == -1) {
            readNotifcations.push(id);

            this.notifications = this.notifications.filter(n => {
                return readNotifcations.indexOf(n.id) >= 0;
            });

            localStorage.setItem(LocalStorageKey.GlobalNotificationStore, JSON.stringify(readNotifcations));

            this.events.ui.onNotificationsUpdated.emit();
        }
    }

    public async getGlobalNotificationCount(): Promise<number> {
        await this.getNotifications();

        var count = 0;

        this.notifications.forEach(n => {
            count += (n.type == NotificationType.Global) ? 1 : 0;
        });

        return count;
    }

    public async getUserNotificationCount(): Promise<number> {
        await this.getNotifications();

        var count = 0;

        this.notifications.forEach(n => {
            count += (n.type != NotificationType.Global) ? 1 : 0;
        });

        return count;
    }

    public async hasNotificationOfType(type: NotificationType): Promise<boolean> {
        await this.getNotifications();

        for (let i = 0; i < this.notifications.length; ++i) {
            if (this.notifications[i].type == type) {
                return true;
            }
        }

        return false;
    }

    public async getNotificationsOfType(type: NotificationType): Promise<INotification[]> {
        await this.getNotifications();

        var notificationsOfType: INotification[] = [];

        this.notifications.forEach(n => {
            if (n.type == type) {
                notificationsOfType.push(n);
            }
        });

        return notificationsOfType;
    }

    public async clearUserNotification(id: string): Promise<boolean> {
        await this.getNotifications();
        
        var result = await firstValueFrom(this.ponderDataClient.notificationUser(id));

        if (result.success) {
            var index = this.notifications.findIndex(n => { return n.id == id; });

            if (index >= 0) {
                this.notifications.splice(index, 1);
            }

            this.events.ui.onNotificationsUpdated.emit();

            return true;
        } else {
            return false;
        }
    }

    public async clearUserNotificationsOfType(type: NotificationType): Promise<boolean> {
        await this.getNotifications();

        var result = await firstValueFrom(this.ponderDataClient.notificationUserType(type));

        if (result.success) {
            this.notifications = this.notifications.filter(n => {
                return n.type != type;
            });
            
            this.events.ui.onNotificationsUpdated.emit();

            return true;
        }
        else {
            return false;
        }
    }

    private getReadGlobalNotifications(): string[] {
        var readStr = getFromLocalStorage(LocalStorageKey.GlobalNotificationStore);

        if (readStr) {
            return JSON.parse(readStr) as string[];
        } else {
            return [];
        }
    }
}